/**
 * Nav backbone FR -> EN dictionary (cabinet portal).
 *
 * The config-driven navigation (`config/contexts.ts`, `config/mega-menu.ts`,
 * `config/right-panel.ts`, `config/quick-nav.ts`) plus the shell chrome
 * (Sidebar bottom nav, ProjectStack shapes, TopBar/feature-bar labels) are all
 * authored in French. Rather than restructure ~520 data entries into keyed
 * objects, we translate at the render choke-points via `tn()`, which maps the
 * French source string to its professional English GRC equivalent.
 *
 * Why a value-keyed map (vs. i18next JSON keys):
 *   - The French source strings are the SINGLE SOURCE OF TRUTH for FR (no
 *     duplication / drift). French IS the default; this table only supplies EN.
 *   - `fallbackLng: 'fr'` semantics are preserved: any French string missing
 *     from this table renders as-is (French) in EN mode — a graceful FR tail.
 *   - Reactivity is driven by i18next: components that read nav labels call
 *     `useTranslation()` (subscribing to `languageChanged`) and re-invoke the
 *     builders, so `tn()` re-evaluates against the new `i18n.language`.
 *
 * GRC terminology is held consistent: risque->risk, conformité->compliance,
 * gouvernance->governance, contrôle->control, référentiel->framework,
 * exigence->requirement, preuve->evidence, constat->finding, mandat->engagement.
 */

import i18n from '@/i18n'

export const NAV_EN: Record<string, string> = {
  // ── Context (persona) names ───────────────────────────────────────────────
  'Associe': 'Partner',
  'Directeur de Mission': 'Engagement Director',
  'Consultant GRC': 'GRC Consultant',
  'Expert Securite': 'Security Expert',
  'Expert Donnees & IA': 'Data & AI Expert',
  'Auditeur': 'Auditor',
  'Analyste': 'Analyst',
  'Formateur': 'Trainer',
  'Administration': 'Administration',

  // ── Context descriptions ──────────────────────────────────────────────────
  'Portefeuille, revenus, prospection': 'Portfolio, revenue, prospecting',
  'Mandats, supervision, livrables': 'Engagements, supervision, deliverables',
  'Risques, conformite, gouvernance': 'Risk, compliance, governance',
  'Audits techniques, ISO 27001, incidents': 'Technical audits, ISO 27001, incidents',
  'RGPD, data governance, IA responsable': 'GDPR, data governance, responsible AI',
  'Missions audit, certification, constats': 'Audit engagements, certification, findings',
  'Evaluations, maturite, benchmarks': 'Assessments, maturity, benchmarks',
  'Sensibilisation, changement, adoption': 'Awareness, change, adoption',
  'Organisation, equipe, facturation': 'Organization, team, billing',

  // ── Section labels (sidebar + mega-menu, accented & non-accented variants) ─
  'Portefeuille': 'Portfolio',
  'Prospection & Developpement': 'Prospecting & Development',
  'Prospection': 'Prospecting',
  'Revenus & Facturation': 'Revenue & Billing',
  'Revenus': 'Revenue',
  'Strategie Cabinet': 'Firm Strategy',
  'Stratégie Cabinet': 'Firm Strategy',
  'Dashboards Executifs': 'Executive Dashboards',
  'Dashboards Exec.': 'Exec. Dashboards',
  'Mes Mandats': 'My Engagements',
  'Planification': 'Planning',
  'Suivi & Qualite': 'Tracking & Quality',
  'Suivi & Qualité': 'Tracking & Quality',
  'Gouvernance': 'Governance',
  'Conformite': 'Compliance',
  'Conformité': 'Compliance',
  'Risques': 'Risk',
  'Mes Missions': 'My Assignments',
  'Ethique & ESG': 'Ethics & ESG',
  'Éthique & ESG': 'Ethics & ESG',
  'Fournisseurs (TPRM)': 'Vendors (TPRM)',
  'Fournisseurs': 'Vendors',
  'Securite': 'Security',
  'Sécurité': 'Security',
  'Continuite & Resilience': 'Continuity & Resilience',
  'Continuité': 'Continuity',
  'Referentiels Securite': 'Security Frameworks',
  'Référentiels Sécu.': 'Security Frameworks',
  'Gouvernance Donnees': 'Data Governance',
  'IA Responsable': 'Responsible AI',
  'Vie Privee & RGPD': 'Privacy & GDPR',
  'Privacy / RGPD': 'Privacy / GDPR',
  'Planification Audit': 'Audit Planning',
  'Plan. Audit': 'Audit Plan.',
  'Execution Audit': 'Audit Execution',
  'Exéc. Audit': 'Audit Exec.',
  'Certification': 'Certification',
  'Evaluations': 'Assessments',
  'Évaluations': 'Assessments',
  'Maturite': 'Maturity',
  'Maturité': 'Maturity',
  'Dashboards & Analytique': 'Dashboards & Analytics',
  'Dashboards Analyste': 'Analyst Dashboards',
  'Gestion du Changement': 'Change Management',
  'Changement': 'Change',
  'Formation & Sensibilisation': 'Training & Awareness',
  'Formation': 'Training',
  'Ethique & Culture': 'Ethics & Culture',
  'Éthique & Culture': 'Ethics & Culture',
  'Workspace': 'Workspace',
  'Rendez-vous': 'Appointments',
  'Rapports': 'Reports',
  'Ma Page': 'My Page',
  'Mon Cabinet': 'My Firm',
  'Equipe': 'Team',
  'Équipe': 'Team',
  'Facturation & Abonnement': 'Billing & Subscription',
  'Facturation': 'Billing',
  'Integrations': 'Integrations',
  'Intégrations': 'Integrations',
  'AIGOV360 - Veille & Packs': 'AIGOV360 - Watch & Packs',
  'AIGOV360 Veille': 'AIGOV360 Watch',
  'Juridique': 'Legal',
  'Parametres': 'Settings',
  'Paramètres': 'Settings',
  'AI Copilot': 'AI Copilot',
  'Communaute & Social': 'Community & Social',
  'Communaute': 'Community',
  'Communauté': 'Community',
  'Outils & Assistance': 'Tools & Assistance',
  'Clients': 'Clients',
  'Copilot Associé': 'Partner Copilot',
  'Copilot Directeur': 'Director Copilot',
  'Copilot Consultant': 'Consultant Copilot',
  'Copilot Sécurité': 'Security Copilot',
  'Copilot Données': 'Data Copilot',
  'Copilot Auditeur': 'Auditor Copilot',
  'Copilot Analyste': 'Analyst Copilot',
  'Copilot Formateur': 'Trainer Copilot',

  // ── Nav items: Portefeuille / Vue globale ────────────────────────────────
  'Dashboard Cabinet': 'Firm Dashboard',
  'Mes Clients': 'My Clients',
  'Vue Globale': 'Global View',
  'Tous les Dossiers': 'All Files',
  'Documents': 'Documents',
  'Signatures': 'Signatures',
  'Calendrier': 'Calendar',
  'Messages': 'Messages',

  // ── Prospection ──────────────────────────────────────────────────────────
  'Pipeline Prospection': 'Prospecting Pipeline',
  'Marketing': 'Marketing',
  'Offres & Devis': 'Offers & Quotes',
  'Relations': 'Relationships',
  'Contrats': 'Contracts',
  'Nouveau Mandat': 'New Engagement',

  // ── Revenus / Facturation / Credits / Plans ──────────────────────────────
  'Factures': 'Invoices',
  'Mes Factures': 'My Invoices',
  'Moyens de Paiement': 'Payment Methods',
  'Recus Fiscaux': 'Tax Receipts',
  'Credits & Quotas': 'Credits & Quotas',
  'Credits IA': 'AI Credits',
  'Acheter Credits': 'Buy Credits',
  'Acheter': 'Buy',
  'Historique': 'History',
  'Usage': 'Usage',
  'Quotas': 'Quotas',
  'Plans & Abonnement': 'Plans & Subscription',
  'Abonnement & Plans': 'Subscription & Plans',
  'Mon Abonnement': 'My Subscription',
  'Plans Disponibles': 'Available Plans',
  'Plans': 'Plans',
  'Modules Actifs': 'Active Modules',
  'Modules actifs': 'Active Modules',
  'Credits': 'Credits',
  'Transfert': 'Transfer',
  'Abonnement': 'Subscription',

  // ── Strategie ────────────────────────────────────────────────────────────
  'Cockpit OKR': 'OKR Cockpit',
  'Cockpit PSA': 'PSA Cockpit',
  'Vision & Analyse': 'Vision & Analysis',
  'Vision': 'Vision',
  'PESTEL': 'PESTEL',
  'SWOT': 'SWOT',
  'Carte Strategique': 'Strategy Map',
  'Execution': 'Execution',
  'Exécution': 'Execution',
  'Business Case': 'Business Case',
  'ROI': 'ROI',
  'Objectifs': 'Objectives',
  'Key Results': 'Key Results',
  'Initiatives': 'Initiatives',
  'Portfolio': 'Portfolio',
  'Business Model': 'Business Model',
  'Business Model Canvas': 'Business Model Canvas',
  'Lean Canvas': 'Lean Canvas',
  'Value Proposition': 'Value Proposition',

  // ── Dashboards exec ──────────────────────────────────────────────────────
  'Panorama': 'Overview',
  'Vues Executives': 'Executive Views',
  'CEO / Panorama': 'CEO / Overview',
  'CFO / C-Suite': 'CFO / C-Suite',
  'Score Global': 'Global Score',
  'KPIs': 'KPIs',
  'Multi-Site': 'Multi-Site',
  'Analytique': 'Analytics',
  'Benchmarks': 'Benchmarks',
  'Tendances': 'Trends',
  'Correlations': 'Correlations',
  'Heatmap': 'Heatmap',
  'Score Consolide': 'Consolidated Score',
  'Rapport CA': 'Board Report',
  'Feuilles de temps': 'Timesheets',

  // ── Mandats / clients / planification ────────────────────────────────────
  'Dashboard Mandats': 'Engagements Dashboard',
  'Dossiers & Mandats': 'Files & Engagements',
  'Repertoire Clients': 'Client Directory',
  'Pipeline': 'Pipeline',
  'Offres': 'Offers',
  'Roadmap': 'Roadmap',
  'Programmes': 'Programs',
  'Programme Data': 'Data Program',
  'Programme IA': 'AI Program',
  'Programme Cloud': 'Cloud Program',

  // ── Suivi & qualite ──────────────────────────────────────────────────────
  'Constats Ouverts': 'Open Findings',
  'Actions Correctives': 'Corrective Actions',
  'Suivi': 'Tracking',
  'Preuves': 'Evidence',
  'Dashboard Cert': 'Cert. Dashboard',
  'Readiness': 'Readiness',
  'Timeline': 'Timeline',

  // ── Gouvernance ──────────────────────────────────────────────────────────
  'Cockpit GRC': 'GRC Cockpit',
  'Cadre & Structure': 'Framework & Structure',
  'Cadre de Reference': 'Reference Framework',
  'Standards': 'Standards',
  'Modele Gouvernance': 'Governance Model',
  'Politiques & Directives': 'Policies & Guidelines',
  'Politiques': 'Policies',
  'Procedures': 'Procedures',
  'Directives': 'Guidelines',
  'RACI & Roles': 'RACI & Roles',
  'Analyse': 'Analysis',
  'Impact IA': 'AI Impact',
  'Analyse Strategique': 'Strategic Analysis',
  'Benchmark': 'Benchmark',
  'Alignement': 'Alignment',
  'Operations': 'Operations',
  'Processus': 'Processes',
  'Générateur de Workflows': 'Workflow Builder',
  'Gouvernance Ops': 'Ops Governance',
  'Registre Risques': 'Risk Register',
  'Controles & Audit': 'Controls & Audit',

  // ── Conformite ───────────────────────────────────────────────────────────
  'Cockpit Multi-Reg': 'Multi-Reg Cockpit',
  'Reglementations': 'Regulations',
  'RGPD': 'GDPR',
  'Loi 25': 'Law 25',
  'CCPA': 'CCPA',
  'PCI-DSS': 'PCI-DSS',
  'SOX': 'SOX',
  'ISO 27701': 'ISO 27701',
  'Gestion': 'Management',
  'Registre': 'Register',
  'Gap Analysis': 'Gap Analysis',
  'Posture': 'Posture',
  'Vie Privee': 'Privacy',
  'Vie privee': 'Privacy',
  'Consentement': 'Consent',
  'Droits (DSR)': 'Rights (DSR)',
  'PIA (AIPD)': 'PIA (DPIA)',
  'Transferts': 'Transfers',
  'Copilot IA': 'AI Copilot',
  "Analyse d'écart réglementaire": 'Regulatory Gap Analysis',
  'Questionnaire IA': 'AI Questionnaire',

  // ── Risques ──────────────────────────────────────────────────────────────
  'Identification & Analyse': 'Identification & Analysis',
  'Wizard Identification': 'Identification Wizard',
  'Scenarios': 'Scenarios',
  'Analyse Bowtie': 'Bowtie Analysis',
  'Matrice 5x5': '5x5 Matrix',
  'Analyse Quantitative': 'Quantitative Analysis',
  'Simulation Monte-Carlo': 'Monte-Carlo Simulation',
  'Traitement & Suivi': 'Treatment & Tracking',
  'Controles': 'Controls',
  'Contrôles': 'Controls',
  'Plans de Traitement': 'Treatment Plans',
  'KRI & Monitoring': 'KRI & Monitoring',
  'Monitoring Continu': 'Continuous Monitoring',
  'Incidents': 'Incidents',
  'Rapports Risques': 'Risk Reports',
  'Rapport Board': 'Board Report',
  'Rapport Risques': 'Risk Report',
  'Taxonomie': 'Taxonomy',
  'Frameworks': 'Frameworks',

  // ── Mes missions ─────────────────────────────────────────────────────────
  'Dossiers Assignes': 'Assigned Files',

  // ── Ethique ──────────────────────────────────────────────────────────────
  'Code de Conduite': 'Code of Conduct',
  'ESG': 'ESG',
  'Signalements': 'Whistleblowing',
  'Anti-Corruption': 'Anti-Corruption',
  'ISO 37001': 'ISO 37001',
  'Sapin II': 'Sapin II',
  "Conflit d'Interets": 'Conflict of Interest',
  'Culture': 'Culture',
  'Culture Ethique': 'Ethics Culture',
  'Culture Éthique': 'Ethics Culture',
  'Diversite': 'Diversity',
  'Diversite & Inclusion': 'Diversity & Inclusion',
  'Comite Ethique': 'Ethics Committee',

  // ── Fournisseurs (TPRM) ──────────────────────────────────────────────────
  'Cockpit TPRM': 'TPRM Cockpit',
  'Framework TPRM': 'TPRM Framework',
  'Due Diligence': 'Due Diligence',
  'Catalogue': 'Catalog',
  'Scorecards': 'Scorecards',
  // ── Évaluation & 360° module ──────────────────────────────────────────────
  'Evaluation & 360°': 'Evaluation & 360°',
  'Vue d\'ensemble': 'Overview',
  'Criteres & modeles': 'Criteria & templates',
  'Cycles': 'Cycles',
  'Scorecards & classements': 'Scorecards & rankings',
  'Plans d\'action': 'Action plans',
  'Contrats & SLA': 'Contracts & SLA',
  'Suivi SLA': 'SLA Monitoring',
  'Renouvellements': 'Renewals',
  'Performance': 'Performance',
  'Concentration': 'Concentration',
  'RGPD Fournisseurs': 'Vendor GDPR',
  'Sous-Traitants': 'Subprocessors',

  // ── Securite ─────────────────────────────────────────────────────────────
  'Hub Securite': 'Security Hub',
  'Hub Sécurité': 'Security Hub',
  'Cockpit NIST CSF': 'NIST CSF Cockpit',
  'Score Securite': 'Security Score',
  'Score sécurité': 'Security Score',
  'Alertes': 'Alerts',
  'Threat Intelligence': 'Threat Intelligence',
  'Vulnerabilites': 'Vulnerabilities',
  'Vulnérabilités': 'Vulnerabilities',
  'Plan Continuite (BCP)': 'Continuity Plan (BCP)',
  'Reprise Sinistre (DR)': 'Disaster Recovery (DR)',
  'Exercices & Tests': 'Exercises & Tests',
  'ISO 27001': 'ISO 27001',
  'SOC 2': 'SOC 2',
  'Templates': 'Templates',
  'Rapport Executif': 'Executive Report',
  'Rapport Operationnel': 'Operational Report',
  'BCP/DR': 'BCP/DR',

  // ── Donnees ──────────────────────────────────────────────────────────────
  'Cockpit DAMA': 'DAMA Cockpit',
  'Score Donnees': 'Data Score',
  'Classification': 'Classification',
  'AI Readiness': 'AI Readiness',
  'Gouvernance Data': 'Data Governance',
  'Qualite Donnees': 'Data Quality',
  'Qualité': 'Quality',
  'Qualité des données': 'Data Quality',
  'Master Data (MDM)': 'Master Data (MDM)',
  'MDM': 'MDM',
  'Metadata & Lineage': 'Metadata & Lineage',
  'Modelisation': 'Modeling',
  'Infrastructure': 'Infrastructure',
  'Architecture': 'Architecture',
  'Stockage': 'Storage',
  'Securite Donnees': 'Data Security',
  'Cycle de Vie': 'Lifecycle',
  'Pipelines': 'Pipelines',
  'DWH & BI': 'DWH & BI',
  'DCAM': 'DCAM',
  'CDMC': 'CDMC',

  // ── IA responsable ───────────────────────────────────────────────────────
  'Console Agents IA': 'AI Agents Console',
  'Cockpit IA': 'AI Cockpit',
  'Score IA': 'AI Score',
  'Bias Assessment': 'Bias Assessment',
  'Impact Assessment': 'Impact Assessment',
  'Document Intelligence': 'Document Intelligence',
  'Gouvernance IA': 'AI Governance',
  'Registre Modeles': 'Model Register',
  'Registre Modèles': 'Model Register',
  'Registre des modèles IA': 'AI Model Register',
  'Detection Biais': 'Bias Detection',
  'Impact': 'Impact',
  'Readiness IA': 'AI Readiness',
  'Classification Risque': 'Risk Classification',
  'Gouvernance Prompts': 'Prompt Governance',
  'Referentiels IA': 'AI Frameworks',
  'EU AI Act': 'EU AI Act',
  'ISO 42001': 'ISO 42001',
  'NIST AI RMF': 'NIST AI RMF',

  // ── Conformite privacy ───────────────────────────────────────────────────
  'Gestion Droits': 'Rights Management',
  'Droits Personnes (DSR)': 'Data Subject Rights (DSR)',
  'DSR': 'DSR',
  'Fournisseurs Privacy': 'Privacy Vendors',

  // ── Audit ────────────────────────────────────────────────────────────────
  'Plan Annuel': 'Annual Plan',
  'Wizard Planification': 'Planning Wizard',
  "Univers d'Audit": 'Audit Universe',
  'Methodologie': 'Methodology',
  'Calendrier Audit': 'Audit Calendar',
  'Cockpit Audit': 'Audit Cockpit',
  'Missions': 'Engagements',
  'Constats': 'Findings',
  'Checklists': 'Checklists',
  'Engagement': 'Engagement',
  'Auto-Evaluation': 'Self-Assessment',
  'Rapport Audit': 'Audit Report',
  'Comite Audit': 'Audit Committee',
  'Dashboard Certification': 'Certification Dashboard',
  'Analyse Gaps': 'Gap Analysis',
  'Projets': 'Projects',
  'Projets Certification': 'Certification Projects',
  'Wizard Projet': 'Project Wizard',
  'Controles & Preuves': 'Controls & Evidence',
  'Decision': 'Decision',
  'Demandes Preuves': 'Evidence Requests',
  'Exigences Doc': 'Doc Requirements',
  'Organismes': 'Bodies',
  'Surveillance': 'Surveillance',
  'Espace Audit': 'Audit Workspace',
  'Portail Organisme': 'Body Portal',
  'Registre Organismes': 'Body Registry',
  'Wizard Audit': 'Audit Wizard',

  // ── Evaluations / maturite ───────────────────────────────────────────────
  'Evaluations Client': 'Client Assessments',
  'Gap Analyse': 'Gap Analysis',
  'Progression': 'Progress',
  'Score': 'Score',
  'Cockpit Maturite': 'Maturity Cockpit',
  'Cockpit Maturité': 'Maturity Cockpit',
  'DCMM (Data/IA/Cloud)': 'DCMM (Data/AI/Cloud)',
  'Modeles': 'Models',
  'CMMI': 'CMMI',
  'COBIT': 'COBIT',
  'ISO 33001': 'ISO 33001',
  'Modele Personnalise': 'Custom Model',
  'Dimensions': 'Dimensions',
  'Mapping': 'Mapping',
  'Resultats': 'Results',
  'Benchmarking': 'Benchmarking',
  'Campagnes': 'Campaigns',
  'Campagnes de maturité': 'Maturity Campaigns',

  // ── Dashboards analyste ──────────────────────────────────────────────────
  'Drill-Down': 'Drill-Down',
  'Comparaison': 'Comparison',
  'Maturite Globale': 'Global Maturity',
  'Conformite Unifiee': 'Unified Compliance',
  'Risque Agregee': 'Aggregated Risk',

  // ── Changement ───────────────────────────────────────────────────────────
  'Cockpit Changement': 'Change Cockpit',
  'ADKAR': 'ADKAR',
  'Kotter 8 Etapes': 'Kotter 8 Steps',
  'Lewin-Bridges': 'Lewin-Bridges',
  'McKinsey 7S': 'McKinsey 7S',
  'Champions': 'Champions',
  'Adoption': 'Adoption',
  'Resistance': 'Resistance',
  'Communication': 'Communication',
  'eNPS': 'eNPS',
  'Mesure': 'Measurement',

  // ── Formation ────────────────────────────────────────────────────────────
  'Formation Changement': 'Change Training',
  'Formation Ethique': 'Ethics Training',
  'Sensibilisation': 'Awareness',
  'Diversite & Inclusion ': 'Diversity & Inclusion',

  // ── Workspace ────────────────────────────────────────────────────────────
  'Chat IA': 'AI Chat',
  'Agents IA': 'AI Agents',
  'Recherche': 'Search',

  // ── Rendez-vous ──────────────────────────────────────────────────────────
  'Calendrier & Disponibilités': 'Calendar & Availability',
  'Rendez-vous reçus': 'Received Appointments',

  // ── Rapports ─────────────────────────────────────────────────────────────
  'Framework Engine': 'Framework Engine',
  'Regie Publicitaire': 'Ad Manager',

  // ── Ma page ──────────────────────────────────────────────────────────────
  'Builder': 'Builder',
  'Apercu public': 'Public Preview',
  'Espaces RAG': 'RAG Spaces',

  // ── Administration ───────────────────────────────────────────────────────
  'Organisations': 'Organizations',
  'Hierarchie': 'Hierarchy',
  'Audit Interne': 'Internal Audit',
  'Roles & Permissions': 'Roles & Permissions',
  'Rôles': 'Roles',
  'Invitations': 'Invitations',
  'Cles API': 'API Keys',
  'Clés API': 'API Keys',
  'Webhooks': 'Webhooks',
  'Sources de Veille': 'Watch Sources',
  'Packs de Juridiction': 'Jurisdiction Packs',
  'Routage & Digests': 'Routing & Digests',
  'Statuts Legaux': 'Legal Statuses',
  'Changements de Pack (PCR)': 'Pack Changes (PCR)',
  'KPIs de Veille': 'Watch KPIs',
  'Hub Juridique': 'Legal Hub',
  'Veille Reglementaire': 'Regulatory Watch',
  'Obligations': 'Obligations',
  'Protection Donnees': 'Data Protection',
  'Contentieux': 'Litigation',
  'Gouvernance Corp.': 'Corp. Governance',
  'Fiscalite': 'Taxation',
  'Profil': 'Profile',
  'Developpeur': 'Developer',

  // ── Copilot: Associe ─────────────────────────────────────────────────────
  'Pipeline Intelligence': 'Pipeline Intelligence',
  'Prevision Revenus': 'Revenue Forecast',
  'Sante Clients': 'Client Health',
  'Cross-Sell IA': 'AI Cross-Sell',
  'Benchmark Cabinet': 'Firm Benchmark',
  'Deal Room': 'Deal Room',
  'NPS/CSAT Clients': 'Client NPS/CSAT',
  'Attribution Revenus': 'Revenue Attribution',
  'Analyse Win/Loss': 'Win/Loss Analysis',
  'Ecosysteme Partenaires': 'Partner Ecosystem',

  // ── Copilot: Directeur ───────────────────────────────────────────────────
  'Planificateur Mission': 'Engagement Planner',
  'Portes Qualite': 'Quality Gates',
  'Redaction Livrables': 'Deliverable Drafting',
  'Hub Communications': 'Communications Hub',
  'Suivi Temps/Budget': 'Time/Budget Tracker',
  'Lecons Apprises': 'Lessons Learned',
  'Optimiseur Ressources': 'Resource Optimizer',
  'Detecteur Scope Creep': 'Scope Creep Detector',
  'Suivi Attentes Client': 'Client Expectation Tracker',
  'Retrospective IA': 'AI Retrospective',
  'Score Qualite Livrables': 'Deliverable Quality Score',

  // ── Copilot: Consultant ──────────────────────────────────────────────────
  'Mappeur Referentiels': 'Framework Mapper',
  'Assistant Tests Controle': 'Control Test Wizard',
  'Questionnaire Adaptatif': 'Adaptive Questionnaire',
  'Collecte Preuves Auto': 'Auto Evidence Collector',
  'Radar Reglementaire': 'Regulatory Radar',
  'Analyseur Transcriptions': 'Transcript Analyzer',
  'Revue IA Papiers Travail': 'AI Working-Paper Review',
  'Verificateur References': 'Cross-Reference Checker',
  'Recherche Reglementaire': 'Regulatory Search',
  'Intelligence Templates': 'Template Intelligence',

  // ── Copilot: Securite ────────────────────────────────────────────────────
  'Flux Menaces': 'Threat Feed',
  'Analyseur Pentest': 'Pentest Parser',
  'Simulateur PCA/PRA': 'BCP/DR Simulator',
  'Maturite SOC': 'SOC Maturity',
  'Lieur Controles-Conformite': 'Control-Compliance Linker',
  'Planificateur Red Team': 'Red Team Planner',
  'Risque Cyber Supply Chain': 'Supply Chain Cyber Risk',
  'Revue Architecture Secu': 'Security Architecture Review',
  'Forensics IA Incidents': 'AI Incident Forensics',

  // ── Copilot: Donnees ─────────────────────────────────────────────────────
  'Decouverte Lignage': 'Lineage Discovery',
  'Score Risque Modeles IA': 'AI Model Risk Score',
  'Detecteur Shadow AI': 'Shadow AI Detector',
  'Generateur AIPD': 'DPIA Generator',
  'Dashboard Qualite Donnees': 'Data Quality Dashboard',
  'Evolution Schemas': 'Schema Evolution',
  'Catalogue Data Products': 'Data Product Catalog',
  'Cout par Requete': 'Cost per Query',
  'Monitoring DataOps': 'DataOps Monitoring',

  // ── Copilot: Auditeur ────────────────────────────────────────────────────
  'Univers Audit IA': 'AI Audit Universe',
  'Generateur Papiers Travail': 'Working-Paper Generator',
  'Classifieur Constats': 'Finding Classifier',
  'Narrateur Rapports': 'Report Narrator',
  'Suivi IA Recommandations': 'AI Recommendation Tracking',
  'Moniteur Audit Continu': 'Continuous Audit Monitor',
  'Echantillonnage Statistique': 'Statistical Sampling',
  'Detecteur Fraude': 'Fraud Detector',
  'Blockchain Preuves': 'Evidence Blockchain',
  'Dashboard CAE': 'CAE Dashboard',

  // ── Copilot: Analyste ────────────────────────────────────────────────────
  'Analytique Predictive': 'Predictive Analytics',
  'Moteur Correlation': 'Correlation Engine',
  'Requete Langage Naturel': 'Natural Language Query',
  'Benchmark Auto': 'Auto Benchmark',
  'Simulation Monte Carlo': 'Monte Carlo Simulation',
  'Reseau Bayesien': 'Bayesian Network',
  'Analyse Cohortes': 'Cohort Analysis',
  'Constructeur What-If': 'What-If Builder',

  // ── Copilot: Formateur ───────────────────────────────────────────────────
  'Generateur Contenus': 'Content Generator',
  'Predicteur Adoption': 'Adoption Predictor',
  'Micro-Learning': 'Micro-Learning',
  'Hub LMS': 'LMS Hub',
  'Matrice Competences': 'Competency Matrix',
  'Parcours Certification': 'Certification Pathway',
  'Analytics Apprentissage': 'Learning Analytics',

  // ── Communaute / transverse ──────────────────────────────────────────────
  "Fil d'actualite": 'Activity Feed',
  'Forum': 'Forum',
  'Reservations & RDV': 'Bookings & Appointments',
  'Messagerie': 'Messaging',
  'Notifications': 'Notifications',
  'Assistant IA': 'AI Assistant',
  'Automatisation (RPA)': 'Automation (RPA)',
  "Centre d'aide": 'Help Center',

  // ── Sidebar bottom nav (firm/client admin, account, settings, legal) ─────
  'Forums': 'Forums',
  'Administration Cabinet': 'Firm Administration',
  'Mon Cabinet ': 'My Firm',
  'Audit & Securite': 'Audit & Security',
  'Admin Client': 'Client Admin',
  'Fiche Client': 'Client Record',
  'Utilisateurs Client': 'Client Users',
  'Roles Client': 'Client Roles',
  'Modules & Contrat': 'Modules & Contract',
  'Facturation Client': 'Client Billing',
  'Audit Client': 'Client Audit',
  'Mon Compte': 'My Account',
  'Apparence': 'Appearance',
  'Juridictions': 'Jurisdictions',
  'Comptabilite': 'Accounting',
  'Preparation fiscale': 'Tax Preparation',
  'Audit & Finance': 'Audit & Finance',
  'Paie': 'Payroll',
  'Propriete Intellectuelle': 'Intellectual Property',
  'Droit du Travail': 'Labor Law',
  'Environnement': 'Environment',
  'Support': 'Support',

  // ── Right-panel card titles + quick-links ────────────────────────────────
  'Accès rapides': 'Quick access',
  'Développement cabinet': 'Firm development',
  'Prospection ': 'Prospecting',
  'Offres commerciales': 'Commercial offers',
  'Portefeuille clients': 'Client portfolio',
  'Pilotage missions': 'Engagement steering',
  'Mandats & missions': 'Engagements & assignments',
  "Constats d'audit": 'Audit findings',
  'Mandats': 'Engagements',
  'Préparation certification': 'Certification readiness',
  'GRC': 'GRC',
  'Score sécurité ': 'Security score',
  'Données & IA': 'Data & AI',
  'Qualité des données ': 'Data quality',
  'Programmes de formation': 'Training programs',
  'Administration ': 'Administration',

  // ── Quick-nav badges + extra titles ──────────────────────────────────────
  'Nouveau': 'New',

  // ── TopBar quick-create actions ──────────────────────────────────────────
  'Nouveau Client': 'New Client',
  'Devis': 'Quote',
  'Mission Audit': 'Audit Engagement',
  'Plan Qualité': 'Quality Plan',
  'Évaluation': 'Assessment',
  'Risque': 'Risk',
  'Gap Analysis ': 'Gap Analysis',
  'Incident': 'Incident',
  'Vulnérabilité': 'Vulnerability',
  'Contrôle': 'Control',
  'PIA': 'PIA',
  'Modèle IA': 'AI Model',
  'Actif Données': 'Data Asset',
  'Constat': 'Finding',
  'Action Corrective': 'Corrective Action',
  'Plan Changement': 'Change Plan',
  'Inviter Membre': 'Invite Member',
  'Organisation': 'Organization',

  // ── ProjectStack shapes (MainLayout) ─────────────────────────────────────
  'Mission audit': 'Audit engagement',
  'Mission GRC': 'GRC engagement',
  'Engagement sécurité': 'Security engagement',
  'Programme Data/IA': 'Data/AI program',
  'Mandat': 'Engagement',
  'Campagne': 'Campaign',
  'Programme': 'Program',
  'Dossier': 'File',
  'Cadrage': 'Scoping',
  'Diagnostic': 'Diagnostic',
  'Plan': 'Plan',
  'Mise en œuvre': 'Implementation',
  'Revue': 'Review',
  'Tests': 'Testing',
  'Remédiation': 'Remediation',
  'Inventaire': 'Inventory',
  'Gaps': 'Gaps',
  'Ouverture': 'Opening',
  'Qualite': 'Quality',
  'Clôture': 'Closing',
  'Collecte': 'Collection',
  'Rapport': 'Report',
  'Design': 'Design',
  'Pilote': 'Pilot',
  'Déploiement': 'Rollout',
}

/**
 * Translate a French nav/shell string to English when the active language is
 * English; otherwise return it unchanged (French is the source of truth, and
 * unknown strings gracefully fall back to French — the honest FR tail).
 *
 * Reactivity: call this from components that subscribe via `useTranslation()`
 * so a `languageChanged` event re-renders and re-evaluates the label.
 */
export function tn(label?: string): string {
  if (!label) return label ?? ''
  const lng = i18n.language || 'fr'
  if (lng.toLowerCase().startsWith('en')) {
    return NAV_EN[label] ?? label
  }
  return label
}
